import React, { useState, useEffect, useRef } from 'react';
import {
  Megaphone, Plus, Loader, Trash2, ArrowLeft, Send, Sparkles, FileText,
  Upload, Users, CheckCircle, XCircle, Clock, RefreshCw, X, Edit3, Mail, PenLine,
} from 'lucide-react';
import {
  getCampaigns, getCampaign, createCampaign, updateCampaign,
  regenerateCampaignQuestions, addCampaignCandidates, removeCampaignCandidate,
  overrideCampaignCandidateQuestions, launchCampaign, deleteCampaign,
  resendCampaignInvitation, parseResumeFile, addNewCandidate,
  getRequirementsList, getOrganizationsList, getCandidatesList,
} from '../services/api';

const DEFAULT_PHASES = { realTimeQA: true, imageQuestions: true, systemDesign: true, codingChallenge: true };

// Backend list endpoints return the raw stored documents, not
// dropdown-shaped objects — normalize once here so the rest of this file can
// keep using a simple { requirement_id, position, experience_level } /
// { organisation_id, name } / { candidate_id, name, email } shape.
const toRequirementOption = (r) => ({
  requirement_id: r.requirementId,
  position: r.jobTitle,
  experience_level: r.experienceLevel,
});
const toOrganisationOption = (o) => ({
  organisation_id: o.organization_id,
  name: o.organizationName,
});
const toCandidateOption = (c) => {
  const pi = c.personal_information || {};
  return {
    candidate_id: c.candidate_id,
    name: `${pi.first_name || ''} ${pi.last_name || ''}`.trim() || 'Unknown',
    email: pi.email || '',
  };
};

function StatusBadge({ status }) {
  const map = {
    sent: { cls: 'bg-green-500/15 text-green-700', Icon: CheckCircle, label: 'Sent' },
    failed: { cls: 'bg-red-500/15 text-red-700', Icon: XCircle, label: 'Failed' },
    pending: { cls: 'bg-yellow-500/15 text-yellow-700', Icon: Clock, label: 'Pending' },
    draft: { cls: 'bg-ink-700 text-ink-300', Icon: Clock, label: 'Draft' },
    launched: { cls: 'bg-brand-500/15 text-brand-700', Icon: Send, label: 'Launched' },
  };
  const { cls, Icon, label } = map[status] || map.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${cls}`}>
      <Icon className="w-3 h-3" /> {label}
    </span>
  );
}

export default function Campaigns() {
  const [view, setView] = useState('list');           // 'list' | 'detail'
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [requirements, setRequirements] = useState([]);
  const [organisations, setOrganisations] = useState([]);
  const [candidates, setCandidates] = useState([]);

  const [showCreate, setShowCreate] = useState(false);
  const [detail, setDetail] = useState(null);          // full campaign object

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [c, r, o, cand] = await Promise.all([
        getCampaigns(),
        getRequirementsList().catch(() => []),
        getOrganizationsList().catch(() => []),
        getCandidatesList().catch(() => []),
      ]);
      setCampaigns(c);
      setRequirements(r.map(toRequirementOption));
      setOrganisations(o.map(toOrganisationOption));
      setCandidates(cand.map(toCandidateOption));
    } catch (e) {
      setError('Failed to load campaigns.');
    } finally {
      setLoading(false);
    }
  };

  const openDetail = async (id) => {
    setLoading(true);
    try {
      const c = await getCampaign(id);
      setDetail(c);
      setView('detail');
    } catch {
      setError('Failed to open campaign.');
    } finally {
      setLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!detail) return;
    const c = await getCampaign(detail.campaign_id);
    setDetail(c);
  };

  if (view === 'detail' && detail) {
    return (
      <CampaignDetail
        campaign={detail}
        requirements={requirements}
        candidates={candidates}
        onBack={() => { setView('list'); setDetail(null); loadAll(); }}
        onRefresh={refreshDetail}
      />
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink-100 flex items-center gap-2">
            <Megaphone className="w-5 h-5 text-brand-600" /> Campaigns
          </h2>
          <p className="text-sm text-ink-300 mt-1">
            One job description → many candidates. Generate questions once, personalize each greeting from the CV, and send all invitations together.
          </p>
        </div>
        <button onClick={() => setShowCreate(true)} className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-400 font-medium">
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-600 px-4 py-3 rounded-lg">{error}</div>}

      {loading ? (
        <div className="flex items-center gap-2 text-ink-400 py-8"><Loader className="w-5 h-5 animate-spin" /> Loading…</div>
      ) : campaigns.length === 0 ? (
        <div className="text-center py-12 text-ink-400 border border-dashed border-ink-700 rounded-lg">
          No campaigns yet. Click <span className="text-brand-600 font-medium">New Campaign</span> to create one.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ink-800">
            <thead className="bg-ink-800">
              <tr>
                {['Name', 'Status', 'Candidates', 'Sent', 'Questions', ''].map((h) => (
                  <th key={h} className="px-6 py-3 text-left text-xs font-medium text-ink-400 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-ink-900 divide-y divide-ink-800">
              {campaigns.map((c) => (
                <tr key={c.campaign_id} className="hover:bg-ink-800 cursor-pointer" onClick={() => openDetail(c.campaign_id)}>
                  <td className="px-6 py-4 text-sm font-medium text-ink-100">{c.name}</td>
                  <td className="px-6 py-4"><StatusBadge status={c.status} /></td>
                  <td className="px-6 py-4 text-sm text-ink-300">{c.candidate_count}</td>
                  <td className="px-6 py-4 text-sm text-ink-300">{c.sent_count}</td>
                  <td className="px-6 py-4 text-sm text-ink-300">{c.base_question_count}</td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-brand-600 text-sm">Open →</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateCampaignModal
          requirements={requirements}
          organisations={organisations}
          onClose={() => setShowCreate(false)}
          onCreated={(c) => { setShowCreate(false); loadAll(); openDetail(c.campaign_id); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────── Create modal ───────────────────────────
function CreateCampaignModal({ requirements, organisations, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [requirementId, setRequirementId] = useState('');
  const [organisationId, setOrganisationId] = useState('');
  const [questionSource, setQuestionSource] = useState('auto'); // 'auto' | 'manual'
  const [totalQuestions, setTotalQuestions] = useState(9);
  const [difficulty, setDifficulty] = useState(5);
  const [phases, setPhases] = useState(DEFAULT_PHASES);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState('');

  const orgList = organisations.filter((o) => o.organisation_id);

  const submit = async () => {
    if (!name.trim() || !requirementId || !organisationId) {
      setErr('Name, job requirement and organization are required.');
      return;
    }
    setSubmitting(true); setErr('');
    try {
      const c = await createCampaign({
        name: name.trim(),
        requirement_id: requirementId,
        organisation_id: organisationId,
        generate_questions: questionSource === 'auto',
        config: { enabled_phases: phases, total_qa_questions: totalQuestions, difficulty_level: difficulty },
      });
      onCreated(c);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to create campaign.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <h3 className="text-lg font-semibold text-ink-100">New Campaign</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4 space-y-4">
          {err && <div className="bg-red-500/10 border border-red-500/30 text-red-600 px-3 py-2 rounded-lg text-sm">{err}</div>}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Campaign Name</label>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Backend Engineer — June batch"
              className="w-full px-4 py-2 border border-ink-700 rounded-lg bg-ink-900 text-ink-100 focus:ring-2 focus:ring-brand-500" />
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Job Requirement (the JD)</label>
            <select value={requirementId} onChange={(e) => setRequirementId(e.target.value)}
              className="w-full px-4 py-2 border border-ink-700 rounded-lg bg-ink-900 text-ink-100 focus:ring-2 focus:ring-brand-500">
              <option value="">Choose a requirement…</option>
              {requirements.map((r) => (
                <option key={r.requirement_id} value={r.requirement_id}>{r.position} — {r.experience_level}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-1">Organization</label>
            <select value={organisationId} onChange={(e) => setOrganisationId(e.target.value)}
              className="w-full px-4 py-2 border border-ink-700 rounded-lg bg-ink-900 text-ink-100 focus:ring-2 focus:ring-brand-500">
              <option value="">Choose an organization…</option>
              {orgList.map((o) => <option key={o.organisation_id} value={o.organisation_id}>{o.name}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-300 mb-2">Interview questions</label>
            <div className="space-y-2">
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${questionSource === 'auto' ? 'border-brand-500 bg-brand-500/5' : 'border-ink-700'}`}>
                <input type="radio" name="questionSource" className="mt-1" checked={questionSource === 'auto'}
                  onChange={() => setQuestionSource('auto')} />
                <span>
                  <span className="flex items-center gap-1.5 text-sm font-medium text-ink-100"><Sparkles className="w-4 h-4 text-brand-600" /> Auto-generate from the JD</span>
                  <span className="block text-xs text-ink-400 mt-0.5">AI writes a shared question set from the job requirement (takes 15–30s). You can still edit it after.</span>
                </span>
              </label>
              <label className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer ${questionSource === 'manual' ? 'border-brand-500 bg-brand-500/5' : 'border-ink-700'}`}>
                <input type="radio" name="questionSource" className="mt-1" checked={questionSource === 'manual'}
                  onChange={() => setQuestionSource('manual')} />
                <span>
                  <span className="flex items-center gap-1.5 text-sm font-medium text-ink-100"><PenLine className="w-4 h-4 text-brand-600" /> I'll add my own questions</span>
                  <span className="block text-xs text-ink-400 mt-0.5">Skip AI generation — start with an empty question set and write your own.</span>
                </span>
              </label>
            </div>
          </div>

          {questionSource === 'auto' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Number of Questions</label>
                <input type="number" min={1} max={20} value={totalQuestions}
                  onChange={(e) => setTotalQuestions(Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                  className="w-full px-3 py-2 border border-ink-700 rounded-lg bg-ink-900 text-ink-100" />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-300 mb-1">Difficulty: {difficulty}/10</label>
                <input type="range" min={1} max={10} value={difficulty}
                  onChange={(e) => setDifficulty(parseInt(e.target.value))}
                  className="w-full h-2 bg-ink-700 rounded-lg appearance-none cursor-pointer accent-brand-600 mt-3" />
              </div>
            </div>
          )}
          <div>
            <label className="block text-sm font-medium text-ink-300 mb-2">Interview Phases</label>
            <div className="space-y-2 bg-ink-800 border border-ink-700 rounded-lg p-3">
              <label className="flex items-center gap-2 text-sm text-ink-400">
                <input type="checkbox" checked disabled className="rounded" /> Phase 1: Real-time Q&A <span className="text-xs text-ink-500 ml-auto">(always on)</span>
              </label>
              {[['imageQuestions', 'Phase 2: Image Questions'], ['systemDesign', 'Phase 3: System Design'], ['codingChallenge', 'Phase 4: Coding Challenge']].map(([k, lbl]) => (
                <label key={k} className="flex items-center gap-2 text-sm text-ink-300 cursor-pointer">
                  <input type="checkbox" checked={phases[k]} onChange={(e) => setPhases((p) => ({ ...p, [k]: e.target.checked }))} className="rounded" /> {lbl}
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-3 border-t border-black/10 p-4">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={submit} disabled={submitting} className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-400 disabled:opacity-50">
            {submitting
              ? <><Loader className="w-4 h-4 animate-spin" /> {questionSource === 'auto' ? 'Generating…' : 'Creating…'}</>
              : <><Sparkles className="w-4 h-4" /> Create Campaign</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Detail view ───────────────────────────
function CampaignDetail({ campaign, requirements, candidates, onBack, onRefresh }) {
  const [questions, setQuestions] = useState(campaign.base_questions || []);
  const [savingQ, setSavingQ] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [launching, setLaunching] = useState(false);
  const [launchResults, setLaunchResults] = useState(null);
  const [error, setError] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [editCand, setEditCand] = useState(null);   // candidate entry being overridden
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => { setQuestions(campaign.base_questions || []); }, [campaign]);

  const reqName = requirements.find((r) => r.requirement_id === campaign.requirement_id)?.position || campaign.requirement_id;

  const updateQ = (i, field, val) => {
    const next = [...questions];
    next[i] = { ...next[i], [field]: val };
    setQuestions(next);
  };
  const addQ = () => setQuestions([...questions, { text: '', expected_keywords: '' }]);
  const removeQ = (i) => setQuestions(questions.filter((_, idx) => idx !== i));

  const saveQuestions = async () => {
    setSavingQ(true); setError('');
    try {
      await updateCampaign(campaign.campaign_id, { base_questions: questions });
      await onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save questions.');
    } finally { setSavingQ(false); }
  };

  const regenerate = async () => {
    if (!window.confirm('Regenerate questions from the JD? This discards manual edits to the shared set.')) return;
    setRegenerating(true); setError('');
    try {
      const { base_questions } = await regenerateCampaignQuestions(campaign.campaign_id);
      setQuestions(base_questions);
      await onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to regenerate.');
    } finally { setRegenerating(false); }
  };

  const launch = async () => {
    const pending = (campaign.candidates || []).filter((c) => c.status !== 'sent').length;
    if (pending === 0) { setError('No pending candidates to send to.'); return; }
    if (!window.confirm(`Send interview invitations to ${pending} candidate(s) now?`)) return;
    setLaunching(true); setError(''); setLaunchResults(null);
    try {
      const res = await launchCampaign(campaign.campaign_id);
      setLaunchResults(res);
      await onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to launch campaign.');
    } finally { setLaunching(false); }
  };

  const removeCand = async (cid) => {
    await removeCampaignCandidate(campaign.campaign_id, cid);
    await onRefresh();
  };

  const resendCand = async (cid) => {
    setResendingId(cid); setError('');
    try {
      await resendCampaignInvitation(campaign.campaign_id, cid);
      await onRefresh();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to resend invitation.');
    } finally { setResendingId(null); }
  };

  const del = async () => {
    if (!window.confirm('Delete this entire campaign? This cannot be undone.')) return;
    await deleteCampaign(campaign.campaign_id);
    onBack();
  };

  const cands = campaign.candidates || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="p-2 rounded-lg hover:bg-ink-800 text-ink-300"><ArrowLeft className="w-5 h-5" /></button>
          <div>
            <h2 className="text-lg font-semibold text-ink-100 flex items-center gap-2">{campaign.name} <StatusBadge status={campaign.status} /></h2>
            <p className="text-sm text-ink-400">Role: {reqName} · {cands.length} candidate(s)</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={del} className="flex items-center gap-2 px-3 py-2 rounded-lg border border-red-500/40 text-red-600 hover:bg-red-500/10 text-sm">
            <Trash2 className="w-4 h-4" /> Delete
          </button>
          <button onClick={launch} disabled={launching}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">
            {launching ? <><Loader className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Launch & Send All</>}
          </button>
        </div>
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-600 px-4 py-3 rounded-lg">{error}</div>}

      {/* Launch results */}
      {launchResults && (
        <div className="border border-green-600/30 bg-green-500/5 rounded-lg p-4">
          <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-green-700">
            <Mail className="w-4 h-4" /> Sent {launchResults.sent} · Failed {launchResults.failed}
          </div>
          <ul className="space-y-1 text-sm">
            {launchResults.results.map((r) => (
              <li key={r.candidate_id} className="flex items-center gap-2 text-ink-300">
                <StatusBadge status={r.status} /> {r.name}
                {r.error && <span className="text-red-600 text-xs">— {r.error}</span>}
                {r.skipped && <span className="text-ink-500 text-xs">(already sent)</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Shared questions */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-ink-100 flex items-center gap-2"><FileText className="w-4 h-4 text-brand-600" /> Shared Questions (from JD)</h3>
          <div className="flex items-center gap-2">
            <button onClick={regenerate} disabled={regenerating} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-ink-700 text-ink-300 hover:bg-ink-800 disabled:opacity-50">
              {regenerating ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Regenerate
            </button>
            <button onClick={saveQuestions} disabled={savingQ} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-brand-500 text-white hover:bg-brand-400 disabled:opacity-50">
              {savingQ ? <Loader className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />} Save
            </button>
          </div>
        </div>
        <p className="text-xs text-ink-400 mb-3">Every candidate is asked these same questions. Their greeting is personalized from their CV automatically.</p>
        <QuestionEditor questions={questions} onUpdate={updateQ} onAdd={addQ} onRemove={removeQ} />
      </div>

      {/* Candidates */}
      <div className="card p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-ink-100 flex items-center gap-2"><Users className="w-4 h-4 text-brand-600" /> Candidates ({cands.length})</h3>
          <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg bg-brand-500/10 text-brand-700 hover:bg-brand-500/20 border border-brand-500/30">
            <Plus className="w-4 h-4" /> Add Candidates
          </button>
        </div>
        {cands.length === 0 ? (
          <p className="text-sm text-ink-500 italic py-3">No candidates yet. Upload resumes or pick existing candidates.</p>
        ) : (
          <div className="divide-y divide-ink-800">
            {cands.map((c) => (
              <div key={c.candidate_id} className="flex items-center gap-3 py-2.5">
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-ink-100 font-medium truncate">{c.name}</p>
                  <p className="text-xs text-ink-400 truncate">{c.email || 'no email'}</p>
                </div>
                {c.questions && <span className="text-xs text-amber-700 flex items-center gap-1"><Edit3 className="w-3 h-3" /> custom Qs</span>}
                <StatusBadge status={c.status} />
                {(c.status === 'sent' || c.status === 'failed') && (
                  <button onClick={() => resendCand(c.candidate_id)} disabled={resendingId === c.candidate_id}
                    title={c.status === 'sent' ? 'Resend the invitation email' : 'Retry sending the invitation'}
                    className="flex items-center gap-1 px-2 py-1 text-xs rounded-lg border border-ink-700 text-ink-300 hover:bg-ink-800 disabled:opacity-50">
                    {resendingId === c.candidate_id
                      ? <Loader className="w-3.5 h-3.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5" />} Resend
                  </button>
                )}
                <button onClick={() => setEditCand(c)} title="Override this candidate's questions" className="p-1.5 text-ink-400 hover:text-brand-600">
                  <Edit3 className="w-4 h-4" />
                </button>
                {c.status !== 'sent' && (
                  <button onClick={() => removeCand(c.candidate_id)} className="p-1.5 text-ink-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AddCandidatesModal
          campaignId={campaign.campaign_id}
          existingCandidates={candidates}
          alreadyIn={cands.map((c) => c.candidate_id)}
          onClose={() => setShowAdd(false)}
          onDone={async () => { setShowAdd(false); await onRefresh(); }}
        />
      )}

      {editCand && (
        <OverrideQuestionsModal
          campaignId={campaign.campaign_id}
          candidate={editCand}
          baseQuestions={questions}
          onClose={() => setEditCand(null)}
          onSaved={async () => { setEditCand(null); await onRefresh(); }}
        />
      )}
    </div>
  );
}

// ─────────────────────────── Question editor (shared) ───────────────────────────
function QuestionEditor({ questions, onUpdate, onAdd, onRemove }) {
  return (
    <div className="space-y-3">
      {questions.length === 0 && <p className="text-sm text-ink-500 italic">No questions yet.</p>}
      {questions.map((q, i) => (
        <div key={i} className="bg-ink-900 border border-ink-700 rounded-lg p-3">
          <div className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-brand-500/15 text-brand-700 flex items-center justify-center text-xs font-bold mt-1">{i + 1}</span>
            <div className="flex-1 space-y-2">
              <textarea rows={2} placeholder="Question text…" value={q.text || ''} onChange={(e) => onUpdate(i, 'text', e.target.value)}
                className="w-full px-3 py-2 border border-ink-700 rounded-lg bg-ink-800 text-ink-100 text-sm focus:ring-2 focus:ring-brand-500" />
              <input placeholder="Expected keywords (optional)" value={q.expected_keywords || ''} onChange={(e) => onUpdate(i, 'expected_keywords', e.target.value)}
                className="w-full px-3 py-2 border border-ink-700 rounded-lg bg-ink-800 text-ink-300 text-xs focus:ring-2 focus:ring-brand-500" />
            </div>
            <button onClick={() => onRemove(i)} className="p-1.5 text-red-600 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        </div>
      ))}
      <button onClick={onAdd} className="flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-ink-700 text-ink-300 hover:bg-ink-800">
        <Plus className="w-4 h-4" /> Add Question
      </button>
    </div>
  );
}

// ─────────────────────────── Add candidates modal ───────────────────────────
function AddCandidatesModal({ campaignId, existingCandidates, alreadyIn, onClose, onDone }) {
  const [tab, setTab] = useState('upload');     // 'upload' | 'existing'
  const [selected, setSelected] = useState([]); // existing candidate_ids
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [err, setErr] = useState('');
  const fileRef = useRef(null);

  const available = existingCandidates.filter((c) => !alreadyIn.includes(c.candidate_id));
  const toggle = (id) => setSelected((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  const addExisting = async () => {
    if (selected.length === 0) { setErr('Select at least one candidate.'); return; }
    setBusy(true); setErr('');
    try {
      await addCampaignCandidates(campaignId, selected);
      await onDone();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to add candidates.');
    } finally { setBusy(false); }
  };

  const uploadResumes = async () => {
    if (files.length === 0) { setErr('Choose resume files first.'); return; }
    setBusy(true); setErr('');
    const newIds = [];
    const failed = [];
    for (let i = 0; i < files.length; i++) {
      const f = files[i];
      setProgress(`Parsing ${i + 1}/${files.length}: ${f.name}…`);
      try {
        const parsed = await parseResumeFile(f);
        const data = parsed.parsed_data || {};
        // Ensure required personal fields exist so candidate creation won't 422.
        data.personal_information = {
          first_name: '', last_name: '', email: '', phone_number: '',
          ...(data.personal_information || {}),
        };
        const created = await addNewCandidate(data);
        const cid = created.candidate_id || created._id;
        if (cid) newIds.push(cid); else failed.push(f.name);
      } catch (e) {
        failed.push(f.name);
      }
    }
    try {
      if (newIds.length > 0) {
        setProgress('Adding to campaign…');
        await addCampaignCandidates(campaignId, newIds);
      }
      if (failed.length > 0) setErr(`Could not process: ${failed.join(', ')}`);
      if (newIds.length > 0) await onDone();
      else setBusy(false);
    } catch (e) {
      setErr('Created candidates but failed to add them to the campaign.');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="card w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <h3 className="text-lg font-semibold text-ink-100">Add Candidates</h3>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="flex border-b border-black/10">
          {[['upload', 'Upload Resumes'], ['existing', 'Existing Candidates']].map(([k, lbl]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-5 py-3 text-sm font-medium border-b-2 ${tab === k ? 'border-brand-500 text-brand-600' : 'border-transparent text-ink-400 hover:text-ink-200'}`}>{lbl}</button>
          ))}
        </div>
        <div className="p-4 space-y-4">
          {err && <div className="bg-red-500/10 border border-red-500/30 text-red-600 px-3 py-2 rounded-lg text-sm">{err}</div>}

          {tab === 'upload' ? (
            <>
              <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-ink-700 rounded-lg p-6 text-center cursor-pointer hover:border-brand-500/50">
                <Upload className="w-8 h-8 text-ink-500 mx-auto mb-2" />
                <p className="text-sm text-ink-300">Click to choose resume files (PDF/DOCX). Multiple allowed.</p>
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" multiple className="hidden"
                  onChange={(e) => setFiles(Array.from(e.target.files || []))} />
              </div>
              {files.length > 0 && (
                <ul className="text-sm text-ink-300 space-y-1">
                  {files.map((f, i) => <li key={i} className="flex items-center gap-2"><FileText className="w-4 h-4 text-ink-500" /> {f.name}</li>)}
                </ul>
              )}
              {busy && progress && <p className="text-sm text-brand-600 flex items-center gap-2"><Loader className="w-4 h-4 animate-spin" /> {progress}</p>}
              <p className="text-xs text-ink-400">Each resume is parsed into a candidate and added to this campaign.</p>
            </>
          ) : (
            <>
              {available.length === 0 ? (
                <p className="text-sm text-ink-500 italic">No more candidates to add.</p>
              ) : (
                <div className="space-y-1 max-h-72 overflow-y-auto bg-ink-800 border border-ink-700 rounded-lg p-3">
                  {available.map((c) => (
                    <label key={c.candidate_id} className="flex items-center gap-3 text-sm text-ink-200 cursor-pointer py-1">
                      <input type="checkbox" checked={selected.includes(c.candidate_id)} onChange={() => toggle(c.candidate_id)} className="rounded" />
                      <span>{c.name} <span className="text-ink-500">({c.email})</span></span>
                    </label>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end gap-3 border-t border-black/10 p-4">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button onClick={tab === 'upload' ? uploadResumes : addExisting} disabled={busy}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-400 disabled:opacity-50">
            {busy ? <><Loader className="w-4 h-4 animate-spin" /> Working…</> : <><Plus className="w-4 h-4" /> Add</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────── Per-candidate override modal ───────────────────────────
function OverrideQuestionsModal({ campaignId, candidate, baseQuestions, onClose, onSaved }) {
  // Seed with the candidate's existing override, else a copy of the shared set.
  const [questions, setQuestions] = useState(
    candidate.questions ? candidate.questions.map((q) => ({ ...q })) : baseQuestions.map((q) => ({ ...q }))
  );
  const [busy, setBusy] = useState(false);
  const hasOverride = !!candidate.questions;

  const updateQ = (i, field, val) => { const n = [...questions]; n[i] = { ...n[i], [field]: val }; setQuestions(n); };
  const addQ = () => setQuestions([...questions, { text: '', expected_keywords: '' }]);
  const removeQ = (i) => setQuestions(questions.filter((_, idx) => idx !== i));

  const save = async () => {
    setBusy(true);
    try {
      await overrideCampaignCandidateQuestions(campaignId, candidate.candidate_id, questions);
      await onSaved();
    } finally { setBusy(false); }
  };
  const reset = async () => {
    setBusy(true);
    try {
      await overrideCampaignCandidateQuestions(campaignId, candidate.candidate_id, null);
      await onSaved();
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <div className="card w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between border-b border-black/10 p-4">
          <div>
            <h3 className="text-lg font-semibold text-ink-100">Questions for {candidate.name}</h3>
            <p className="text-xs text-ink-400">{hasOverride ? 'This candidate has custom questions.' : 'Editing creates a custom set for this candidate only.'}</p>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-100"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-4">
          <QuestionEditor questions={questions} onUpdate={updateQ} onAdd={addQ} onRemove={removeQ} />
        </div>
        <div className="flex justify-between gap-3 border-t border-black/10 p-4">
          <button onClick={reset} disabled={busy || !hasOverride} className="px-4 py-2 rounded-lg border border-ink-700 text-ink-300 hover:bg-ink-800 disabled:opacity-40 text-sm">
            Reset to shared set
          </button>
          <div className="flex gap-3">
            <button onClick={onClose} className="btn-secondary">Cancel</button>
            <button onClick={save} disabled={busy} className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-400 disabled:opacity-50">
              {busy ? <><Loader className="w-4 h-4 animate-spin" /> Saving…</> : <><CheckCircle className="w-4 h-4" /> Save Custom Questions</>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
